import {
  CustomExportRunItemResponse,
  CustomExportRunItemStatus,
  CustomExportRunResponse,
  CustomExportRunStatus,
  CustomExportRunType,
} from '@ydistri/api-sdk';
import type { CustomExportListQuery } from './apiCalculationExport';
import { colors } from '../../../components/global/_constants';

export const defaultCustomExportListQuery: CustomExportListQuery = {
  inlineCount: true,
};

export const sortCustomExportByCreatedAtAsc = <T extends { createdAt: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
export const sortCustomExportByCreatedAtDesc = <T extends { createdAt: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

export const formatCustomExportJson = (
  value: string | null | undefined,
  emptyMessage: string,
): string => {
  if (!value) return emptyMessage;

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

interface CustomExportStatusConfig {
  backgroundColor: string;
  color: string;
  label: string;
}

export type CustomExportStatus = CustomExportRunStatus | CustomExportRunItemStatus;

export const CUSTOM_EXPORT_STATUS_CONFIG: Record<CustomExportStatus, CustomExportStatusConfig> = {
  [CustomExportRunStatus.New]: {
    backgroundColor: colors.blue.light,
    color: colors.blue.default,
    label: 'New',
  },
  [CustomExportRunStatus.Queued]: {
    backgroundColor: colors.blue.light,
    color: colors.blue.default,
    label: 'Queued',
  },
  [CustomExportRunStatus.Preparing]: {
    backgroundColor: colors.orange.light,
    color: colors.orange.default,
    label: 'Preparing',
  },
  [CustomExportRunStatus.Processing]: {
    backgroundColor: colors.orange.light,
    color: colors.orange.default,
    label: 'Processing',
  },
  [CustomExportRunStatus.Revoked]: {
    backgroundColor: colors.grey.light,
    color: colors.grey.default,
    label: 'Revoked',
  },
  [CustomExportRunStatus.Done]: {
    backgroundColor: colors.green.light,
    color: colors.green.default,
    label: 'Done',
  },
  [CustomExportRunStatus.Failed]: {
    backgroundColor: colors.red.light,
    color: colors.red.default,
    label: 'Failed',
  },
  [CustomExportRunStatus.FailedPreparing]: {
    backgroundColor: colors.red.light,
    color: colors.red.default,
    label: 'Failed preparing',
  },
  [CustomExportRunStatus.DoneWithFailed]: {
    backgroundColor: colors.orange.light,
    color: colors.orange.default,
    label: 'Done with failed items',
  },
};

const activeRunStatuses = new Set<CustomExportRunStatus>([
  CustomExportRunStatus.New,
  CustomExportRunStatus.Queued,
  CustomExportRunStatus.Preparing,
  CustomExportRunStatus.Processing,
]);

const doneWithFailedItemStatuses = new Set<CustomExportRunItemStatus>([
  CustomExportRunItemStatus.Done,
  CustomExportRunItemStatus.Failed,
]);

const startableRunStatuses = new Set<CustomExportRunStatus>([
  CustomExportRunStatus.Revoked,
  CustomExportRunStatus.FailedPreparing,
]);

export const isCustomExportRunActive = (status?: CustomExportRunStatus | null): boolean =>
  status ? activeRunStatuses.has(status) : false;

export const getCustomExportAvailableActions = (
  latestRun?: Pick<CustomExportRunResponse, 'status'> | null,
): CustomExportRunType[] => {
  if (!latestRun) return [CustomExportRunType.New];

  if (startableRunStatuses.has(latestRun.status)) {
    return [CustomExportRunType.New];
  }
  if (latestRun.status === CustomExportRunStatus.DoneWithFailed) {
    return [CustomExportRunType.RetryErrors];
  } else if (latestRun.status === CustomExportRunStatus.Failed) {
    return [CustomExportRunType.RetryErrors, CustomExportRunType.SkipErrors];
  }

  return [];
};

export const getCustomExportTooltip = (
  latestRun?: Pick<CustomExportRunResponse, 'status'> | null,
): string => {
  const availableActions = getCustomExportAvailableActions(latestRun);

  if (availableActions.includes(CustomExportRunType.SkipErrors)) {
    return 'Retry or skip failed custom export items.';
  }
  if (availableActions.includes(CustomExportRunType.RetryErrors)) {
    return 'Retry failed custom export items.';
  }
  if (availableActions.includes(CustomExportRunType.New)) {
    return latestRun?.status === CustomExportRunStatus.FailedPreparing
      ? 'Custom export failed during preparation. Start a new custom export.'
      : 'Start custom export for this calculation.';
  }
  if (latestRun && isCustomExportRunActive(latestRun.status)) {
    return `Custom export is ${CUSTOM_EXPORT_STATUS_CONFIG[latestRun.status].label.toLowerCase()}.`;
  }
  if (latestRun?.status === CustomExportRunStatus.Done) {
    return 'Custom export finished.';
  }

  return 'Custom export is not available.';
};

export const shouldShowCustomExportEntry = ({
  isCustomExportEnabled,
  isProduction,
  hasRunHistory,
}: {
  isCustomExportEnabled: boolean;
  isProduction: boolean;
  hasRunHistory: boolean;
}): boolean => isProduction && (isCustomExportEnabled || hasRunHistory);

export const shouldShowDoneWithFailedAction = (
  items: Pick<CustomExportRunItemResponse, 'status'>[],
): boolean => {
  const hasDoneItem = items.some(item => item.status === CustomExportRunItemStatus.Done);
  const hasFailedItem = items.some(item => item.status === CustomExportRunItemStatus.Failed);
  const hasOnlyDoneOrFailedItems = items.every(item => doneWithFailedItemStatuses.has(item.status));

  return items.length > 0 && hasDoneItem && hasFailedItem && hasOnlyDoneOrFailedItems;
};
